import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MobileNav } from '../components/MobileNav';
import { ShoppingCart, Plus, Minus, Trash2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config';

const playfairFont = "'Playfair Display', serif";

export const Cart = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchCart(); }, []);

  const fetchCart = async () => {
    try {
      const { data } = await axios.get(`${API}/cart`, { withCredentials: true });
      setCart(data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => new Intl.NumberFormat('id-ID', {
    style: 'currency', currency: 'IDR', minimumFractionDigits: 0
  }).format(price);

  const updateQuantity = async (productId, variantKey, newQuantity, item) => {
    if (newQuantity <= 0) return;
    try {
      await axios.put(`${API}/cart/update`, {
        product_id: productId,
        quantity: newQuantity,
        variant_selection: item.variant_selection || null,
      }, { withCredentials: true });
      await fetchCart();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    }
  };

  const removeItem = async (productId, variantKey) => {
    try {
      await axios.delete(`${API}/cart/remove/${productId}?variant_key=${encodeURIComponent(variantKey || '')}`, { withCredentials: true });
      await fetchCart();
      toast.success('Item removed');
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  const total = cart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  return (
    <div className="min-h-screen pb-24" style={{ background: '#080C0A', fontFamily: "'Outfit', sans-serif" }}>
      <header className="sticky top-0 z-40 backdrop-blur-2xl" style={{ background: 'rgba(8,12,10,0.9)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="hover:opacity-80" data-testid="back-btn">
            <ArrowLeft className="w-6 h-6" style={{ color: '#F8FAF9' }} />
          </button>
          <h1 className="text-2xl font-light" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Shopping Cart</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-12" style={{ color: '#A3B8AC' }}>Loading...</div>
        ) : cart.items.length === 0 ? (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-center py-16 rounded-2xl backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <ShoppingCart className="w-20 h-20 mx-auto mb-4" style={{ color: '#A3B8AC' }} />
            <h2 className="text-xl font-light mb-2" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Your cart is empty</h2>
            <p className="mb-6" style={{ color: '#A3B8AC' }}>Discover premium products</p>
            <motion.button whileTap={{ scale: 0.95 }} onClick={() => navigate('/')} className="px-8 py-3 rounded-full font-semibold" style={{ background: '#D4AF37', color: '#080C0A' }} data-testid="shop-now-btn">
              Shop Now
            </motion.button>
          </motion.div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence>
              {cart.items.map((item) => (
                <motion.div
                  key={`${item.product_id}|${item.variant_key || ''}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="rounded-2xl p-4 backdrop-blur-xl flex gap-4"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
                  data-testid={`cart-item-${item.product_id}`}
                >
                  <img src={item.image_url} alt={item.name} className="w-24 h-24 object-cover rounded-xl" />
                  <div className="flex-1">
                    <h3 className="font-medium mb-1" style={{ color: '#F8FAF9' }} data-testid="cart-item-name">{item.name}</h3>
                    {item.variant_label && (
                      <p className="text-xs mb-1" style={{ color: '#D4AF37' }} data-testid="cart-item-variant">{item.variant_label}</p>
                    )}
                    <p className="font-bold mb-3" style={{ color: '#D4AF37', fontFamily: playfairFont }} data-testid="cart-item-price">{formatPrice(item.price)}</p>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 rounded-xl p-1" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <button onClick={() => updateQuantity(item.product_id, item.variant_key, item.quantity - 1, item)} className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: 'rgba(212,175,55,0.15)', color: '#D4AF37' }} data-testid="decrease-quantity-btn">
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center font-semibold" style={{ color: '#F8FAF9' }} data-testid="cart-item-quantity">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.product_id, item.variant_key, item.quantity + 1, item)} className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: 'rgba(212,175,55,0.15)', color: '#D4AF37' }} data-testid="increase-quantity-btn">
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <button onClick={() => removeItem(item.product_id, item.variant_key)} className="ml-auto p-2 rounded-lg" style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444' }} data-testid="remove-item-btn">
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            <div className="rounded-2xl p-6 mt-6 backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div className="flex justify-between items-center mb-4">
                <span style={{ color: '#A3B8AC' }}>Subtotal</span>
                <span className="font-semibold" style={{ color: '#F8FAF9' }} data-testid="cart-subtotal">{formatPrice(total)}</span>
              </div>
              <div className="flex justify-between items-center mb-6 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <span className="font-medium" style={{ color: '#F8FAF9' }}>Total</span>
                <span className="font-bold text-2xl" style={{ color: '#D4AF37', fontFamily: playfairFont }} data-testid="cart-total">{formatPrice(total)}</span>
              </div>
              <motion.button whileTap={{ scale: 0.98 }} onClick={() => navigate('/checkout')} className="w-full py-4 rounded-full font-semibold" style={{ background: '#D4AF37', color: '#080C0A' }} data-testid="checkout-btn">
                Proceed to Checkout
              </motion.button>
            </div>
          </div>
        )}
      </div>

      <MobileNav />
    </div>
  );
};
